import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/backend/api-client";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertCircle, CheckCircle2, Loader2, Clock, X } from "lucide-react";
import { cn, formatCurrency } from "@/lib/utils";

interface WithdrawalModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
}

interface AvailableAmounts {
  total_earnings: string;
  unlocked_principal: string;
  locked_principal: string;
  earliest_unlock_date?: string;
  can_withdraw_earnings: boolean;
  can_withdraw_principal: boolean;
}

type TabType = "earnings" | "principal";

export function LPWithdrawalModal({ isOpen, onClose, userId }: WithdrawalModalProps) {
  const [activeTab, setActiveTab] = useState<TabType>("earnings");
  const [amount, setAmount] = useState("");
  const [showEarlyWithdrawal, setShowEarlyWithdrawal] = useState(false);
  const [acceptPenalty, setAcceptPenalty] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const queryClient = useQueryClient();

  const { data: available, isLoading: loadingAvailable } = useQuery<AvailableAmounts>({
    queryKey: ["lp-available", userId],
    queryFn: async () => {
      const response = await apiClient.get(`/api/lp/available-withdrawal?user_id=${userId}`);
      return response as AvailableAmounts;
    },
    enabled: isOpen && !!userId,
  });

  const withdrawMutation = useMutation({
    mutationFn: async (data: any) => {
      return await apiClient.post(`/api/lp/withdraw?user_id=${userId}`, data);
    },
    onSuccess: (data: any) => {
      setShowSuccess(true);
      setSuccessMessage(data.message || "Withdrawal request submitted successfully");
      setAmount("");
      setAcceptPenalty(false);
      setShowEarlyWithdrawal(false);
      queryClient.invalidateQueries({ queryKey: ["lp-account", userId] });
      queryClient.invalidateQueries({ queryKey: ["lp-available", userId] });
      
      setTimeout(() => {
        setShowSuccess(false);
        onClose();
      }, 3000);
    },
    onError: (error: any) => {
      console.error("Withdrawal error:", error);
    },
  });

  const earningsAvailable = parseFloat(available?.total_earnings || "0");
  const principalUnlocked = parseFloat(available?.unlocked_principal || "0");
  const principalLocked = parseFloat(available?.locked_principal || "0");
  const penaltyPercent = 5;

  const getMaxAmount = () => {
    if (activeTab === "earnings") return earningsAvailable;
    if (showEarlyWithdrawal) return principalLocked;
    return principalUnlocked;
  };

  const calculatePenalty = () => {
    if (showEarlyWithdrawal && amount) {
      return (parseFloat(amount) * penaltyPercent / 100).toFixed(2);
    }
    return "0";
  };

  const calculateNetAmount = () => {
    if (showEarlyWithdrawal && amount) {
      return (parseFloat(amount) - parseFloat(calculatePenalty())).toFixed(2);
    }
    return amount || "0";
  };

  const handleSubmit = () => {
    const withdrawalType = activeTab === "earnings" 
      ? "earnings_only"
      : showEarlyWithdrawal
      ? "early_withdrawal_with_penalty"
      : "unlocked_principal";

    withdrawMutation.mutate({
      amount,
      withdrawal_type: withdrawalType,
      accept_penalty: showEarlyWithdrawal ? acceptPenalty : undefined,
    });
  };

  const isSubmitDisabled =
    !amount ||
    parseFloat(amount) <= 0 ||
    parseFloat(amount) > getMaxAmount() ||
    (showEarlyWithdrawal && !acceptPenalty) ||
    withdrawMutation.isPending;

  const tabs = [
    { id: "earnings" as const, label: "Withdraw Earnings" },
    { id: "principal" as const, label: "Withdraw Principal" },
  ];

  if (showSuccess) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-[520px]">
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <CheckCircle2 className="h-16 w-16 text-emerald-500 mb-4" />
            <DialogTitle className="text-xl mb-2">Success!</DialogTitle>
            <p className="text-sm text-muted-foreground">{successMessage}</p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[520px] p-0 gap-0 overflow-hidden bg-card border-border" showCloseButton={false}>
        {/* Header with Close Button */}
        <DialogHeader className="px-6 pt-6 pb-4 space-y-2">
          <div className="flex items-center justify-between">
            <DialogTitle className="text-xl">Request Withdrawal</DialogTitle>
            <button
              onClick={() => onClose()}
              className="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none"
            >
              <X className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </button>
          </div>
        </DialogHeader>

        {/* Tabs */}
        <div className="flex items-center border-b border-border/40">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => {
                setActiveTab(tab.id);
                setAmount("");
                setShowEarlyWithdrawal(false);
                setAcceptPenalty(false);
              }}
              className={cn(
                "relative flex-1 px-4 py-3 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer text-center",
                activeTab === tab.id
                  ? "text-emerald-600 dark:text-emerald-400"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              {tab.label}
              {activeTab === tab.id && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-emerald-500" />
              )}
            </button>
          ))}
        </div>

        {loadingAvailable ? (
          <div className="flex items-center justify-center py-12 px-6">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="px-6 py-5 space-y-5 min-h-[340px]">
            {/* Earnings Tab */}
            {activeTab === "earnings" && (
              <>
                {/* Available Amount */}
                <div className="rounded-lg bg-muted/50 p-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Available Earnings</span>
                    <span className="text-lg font-semibold text-emerald-500">
                      ${formatCurrency(earningsAvailable, 2)}
                    </span>
                  </div>
                </div>

                {/* Amount Input */}
                <div className="space-y-2">
                  <Label htmlFor="earnings-amount">Amount (USD)</Label>
                  <div className="relative">
                    <Input
                      id="earnings-amount"
                      type="number"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      placeholder="0.00"
                      step="0.01"
                      min="0"
                      max={earningsAvailable}
                      className="pr-16 h-11"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-1 top-1 h-9 text-xs"
                      onClick={() => setAmount(earningsAvailable.toFixed(2))}
                    >
                      MAX
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Maximum: ${formatCurrency(earningsAvailable, 2)}
                  </p>
                </div>

                {/* Instant Processing Info */}
                {amount && parseFloat(amount) <= 1000 && parseFloat(amount) > 0 && (
                  <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-3">
                    <div className="flex items-start gap-2">
                      <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400 mt-0.5 shrink-0" />
                      <p className="text-sm text-emerald-600 dark:text-emerald-400">
                        This withdrawal will be processed instantly
                      </p>
                    </div>
                  </div>
                )}

                {amount && parseFloat(amount) > 1000 && (
                  <div className="rounded-lg border border-blue-500/30 bg-blue-500/10 p-3">
                    <div className="flex items-start gap-2">
                      <Clock className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5 shrink-0" />
                      <p className="text-sm text-blue-600 dark:text-blue-400">
                        Manual approval required (24-48 hours)
                      </p>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Principal Tab */}
            {activeTab === "principal" && (
              <>
                {/* Available Amounts */}
                <div className="space-y-3 rounded-lg bg-muted/50 p-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Unlocked Principal</span>
                    <span className="text-lg font-semibold">
                      ${formatCurrency(principalUnlocked, 2)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Locked Principal</span>
                    <div className="text-right">
                      <div className="text-lg font-semibold">${formatCurrency(principalLocked, 2)}</div>
                      {available?.earliest_unlock_date && (
                        <div className="text-xs text-muted-foreground mt-0.5">
                          Unlocks {new Date(available.earliest_unlock_date).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Show Early Withdrawal Option Button */}
                {!showEarlyWithdrawal && principalLocked > 0 && (
                  <button
                    onClick={() => setShowEarlyWithdrawal(true)}
                    className="w-full rounded-lg border border-amber-500/30 bg-amber-500/5 px-4 py-3 text-left transition-colors hover:bg-amber-500/10"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm font-medium">Request Early Withdrawal</div>
                        <div className="text-xs text-muted-foreground">
                          Withdraw locked principal with {penaltyPercent}% fee
                        </div>
                      </div>
                      <AlertCircle className="h-5 w-5 text-amber-500 shrink-0" />
                    </div>
                  </button>
                )}

                {/* Amount Input */}
                <div className="space-y-2">
                  <Label htmlFor="principal-amount">Amount (USD)</Label>
                  <div className="relative">
                    <Input
                      id="principal-amount"
                      type="number"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      placeholder="0.00"
                      step="0.01"
                      min="0"
                      max={getMaxAmount()}
                      className="pr-16 h-11"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-1 top-1 h-9 text-xs"
                      onClick={() => setAmount(getMaxAmount().toFixed(2))}
                    >
                      MAX
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {showEarlyWithdrawal 
                      ? `Locked principal: $${formatCurrency(principalLocked, 2)}`
                      : `Unlocked principal: $${formatCurrency(principalUnlocked, 2)}`
                    }
                  </p>
                </div>

                {/* Early Withdrawal Penalty Section */}
                {showEarlyWithdrawal && amount && parseFloat(amount) > 0 && (
                  <div className="space-y-3">
                    <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-4">
                      <div className="flex items-start gap-2 mb-3">
                        <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
                        <div className="flex-1">
                          <div className="font-semibold text-sm text-amber-600 dark:text-amber-400 mb-2">
                            Early Withdrawal Penalty
                          </div>
                          <div className="text-sm text-amber-600/90 dark:text-amber-400/90 space-y-1">
                            <div className="flex justify-between">
                              <span>Requested:</span>
                              <span className="font-medium">${parseFloat(amount).toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span>Penalty ({penaltyPercent}%):</span>
                              <span className="font-medium">-${calculatePenalty()}</span>
                            </div>
                            <div className="flex justify-between pt-2 border-t border-amber-500/20">
                              <span className="font-semibold">You'll receive:</span>
                              <span className="font-semibold">${calculateNetAmount()}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-start space-x-2">
                        <Checkbox
                          id="accept-penalty"
                          checked={acceptPenalty}
                          onCheckedChange={(checked) => setAcceptPenalty(checked as boolean)}
                        />
                        <Label htmlFor="accept-penalty" className="text-sm cursor-pointer leading-relaxed">
                          I understand and accept the {penaltyPercent}% early withdrawal penalty. Manual approval required.
                        </Label>
                      </div>
                    </div>
                  </div>
                )}

                {/* Manual Approval Notice for Regular Principal */}
                {!showEarlyWithdrawal && amount && parseFloat(amount) > 0 && (
                  <div className="rounded-lg border border-blue-500/30 bg-blue-500/10 p-3">
                    <div className="flex items-start gap-2">
                      <Clock className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5 shrink-0" />
                      <p className="text-sm text-blue-600 dark:text-blue-400">
                        Manual approval required (24-48 hours)
                      </p>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Error Display */}
            {withdrawMutation.isError && (
              <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400 mt-0.5 shrink-0" />
                  <p className="text-sm text-red-600 dark:text-red-400">
                    {(withdrawMutation.error as any)?.message || "Failed to submit withdrawal request"}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="flex gap-3 px-6 py-4 border-t border-border/40">
          <Button 
            variant="outline" 
            onClick={() => onClose()} 
            disabled={withdrawMutation.isPending}
            className="flex-1 h-11"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
            className="flex-1 h-11"
          >
            {withdrawMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Processing...
              </>
            ) : (
              "Request Withdrawal"
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
